import { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import FileSectionToggleRow from '@/components/common/FileSectionToggleRow';
import GcodeByControllerRows from '@/components/common/GcodeByControllerRows';
import { isCadVectorFile, getFileIcon, getFileExtension } from '@/lib/fileType';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Toggle } from '@/components/ui/toggle';
import DownloadConsentNotice from '@/components/common/DownloadConsentNotice';
import { cn } from '@/lib/utils';
import { useFileDownloader } from '@/lib/s3';
import { CONTROLLER_CONFIG } from '@/lib/controllerConfig';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import LiabilityWaiverDialog from '@/components/common/LiabilityWaiverDialog';
import HowToDownloadLink from '@/components/common/HowToDownloadLink';
import {
  IProjectGroupProject,
  IProjectGroupProjectLight,
} from '@/redux/projectGroup/projectGroup.d';
import {
  IPostAttachment,
  IAttachmentCategory,
  IControllerType,
} from '@/redux/post/post.d';
import {
  filterAttachments,
  filterAttachmentsByController,
  getAvailableControllers,
} from '@/redux/post/post.utils';
import { useLazyDownloadProjectGroupQuery } from '@/redux/projectGroup/projectGroup.api.slice';

interface DownloadProjectGroupModalProps {
  projectGroupId: string;
  projectGroupName: string;
  projects: (IProjectGroupProject | IProjectGroupProjectLight)[];
  /**
   * Total count of parts across all projects
   * May be undefined until parts data is loaded (deferred loading optimization)
   */
  totalPartsCount?: number;
}

/** One file row under a project's G-code / CAD / Pictures section. */
function AttachmentFileRow({
  attachment,
  projectTitle,
  muted,
  downloadingId,
  onDownload,
}: {
  attachment: IPostAttachment;
  projectTitle: string;
  muted: boolean;
  downloadingId: string | null;
  onDownload: (attachment: IPostAttachment, projectTitle: string) => void;
}) {
  return (
    <TableRow className="border-b-0">
      <TableCell className="hidden sm:table-cell pl-4 py-2">
        {getFileIcon(attachment)}
      </TableCell>
      <TableCell className="py-2 overflow-hidden max-w-0">
        <p className="font-medium leading-none truncate text-sm">
          {attachment.fileName}
        </p>
      </TableCell>
      <TableCell className="text-center text-xs py-2 text-muted-foreground">
        {getFileExtension(attachment.url)}
      </TableCell>
      <TableCell className="py-2">
        <div className="bg-secondary rounded-full">
          <Button
            size="sm"
            variant="default"
            className={cn(
              'w-full text-xs sm:text-sm py-2 px-3 h-auto',
              muted && 'opacity-60'
            )}
            onClick={() => onDownload(attachment, projectTitle)}
            disabled={!!downloadingId}
          >
            {downloadingId === attachment._id ? (
              <Loader2 className="mr-2 h-3 w-3 animate-spin text-white" />
            ) : (
              'Download'
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function DownloadProjectGroupModal({
  projectGroupId,
  projectGroupName,
  projects,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  totalPartsCount: _totalPartsCount,
}: DownloadProjectGroupModalProps) {
  const [openCad, setOpenCad] = useState(false);
  const [openPics, setOpenPics] = useState(false);
  const [open, setOpen] = useState(false);
  const { downloadFile } = useFileDownloader();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [licenceAccepted, setLicenceAccepted] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('ALL');
  const [selectedController, setSelectedController] = useState<
    IControllerType | 'ALL'
  >('ALL');
  const downloadsDisabled = !licenceAccepted;
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(selectCurrentUser);
  const [triggerGroupDownload] = useLazyDownloadProjectGroupQuery();

  const handleDownload = async (
    attachment: IPostAttachment,
    projectTitle: string
  ) => {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    if (downloadsDisabled) {
      setNeedsConsent(true);
      return;
    }
    setDownloadingId(attachment._id);
    try {
      let { fileName } = attachment;
      // If it's a project zip, use the project title as filename
      if (
        attachment.category === IAttachmentCategory.postZip ||
        attachment.resourceType === AttachmentType.zip
      ) {
        const ext = getFileExtension(attachment.url) || 'zip';
        fileName = `${projectTitle.replace(/[^a-zA-Z0-9]/g, '_')}.${ext}`;
      }

      await downloadFile(attachment._id, fileName);
    } catch (error) {
      toast.error('Failed to download file, please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    if (downloadsDisabled) {
      setNeedsConsent(true);
      return;
    }

    setDownloadingId('GROUP_DOWNLOAD');
    try {
      const result = await triggerGroupDownload(projectGroupId).unwrap();

      if (result.signedUrl) {
        const link = document.createElement('a');
        link.href = result.signedUrl;
        link.download = `${projectGroupName.replace(/\./g, '_').replace(/\s+/g, '_')}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Download started', {
          description: `Downloading all files from ${projectGroupName}`,
        });
      }
    } catch (error) {
      toast.error('Download failed', {
        description: 'Failed to generate download. Please try again later.',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const allAttachments = useMemo(
    () => projects.flatMap((p) => p.attachments || []),
    [projects]
  );

  const availableControllers = useMemo(
    () => getAvailableControllers(allAttachments),
    [allAttachments]
  );

  const filteredProjects = useMemo(() => {
    if (selectedProjectId === 'ALL') return projects;
    return projects.filter((p) => p._id === selectedProjectId);
  }, [projects, selectedProjectId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="w-full rounded-full bg-secondary sm:w-auto">
          <Button variant="default" size="md" className="w-full sm:w-auto">
            Download
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-8px)] max-w-[800px] max-h-[85vh] p-4 sm:p-6 flex flex-col gap-0">
        <DialogHeader className="pb-3">
          {/* pr-8 clears the dialog's own close button. */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pr-8">
            <DialogTitle className="text-lg sm:text-2xl font-primary text-left">
              Download Files - {projectGroupName}
            </DialogTitle>
            <HowToDownloadLink />
          </div>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 gap-3">
          {/* Filter Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0">
            <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0 justify-center sm:justify-start">
              <Toggle
                pressed={selectedProjectId === 'ALL'}
                onPressedChange={() => setSelectedProjectId('ALL')}
                className="gap-2 border h-8 px-3 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
              >
                <span>All</span>
              </Toggle>
              {projects.map((project) => (
                <Toggle
                  key={project._id}
                  pressed={selectedProjectId === project._id}
                  onPressedChange={() => setSelectedProjectId(project._id)}
                  className="gap-2 border h-8 px-3 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
                >
                  <span
                    className="truncate max-w-[120px]"
                    title={project.title}
                  >
                    {project.title}
                  </span>
                </Toggle>
              ))}
            </div>

            <Button
              size="sm"
              onClick={handleDownloadAll}
              disabled={!!downloadingId}
              className={cn(
                'rounded-full sm:ml-auto flex-shrink-0 w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/80',
                downloadsDisabled && 'opacity-60'
              )}
            >
              {downloadingId === 'GROUP_DOWNLOAD' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                'Download All'
              )}
            </Button>
          </div>

          {/* Controller Filter */}
          {availableControllers.length > 0 && (
            <div className="flex gap-2 items-center flex-wrap flex-shrink-0 justify-center sm:justify-start">
              <Toggle
                pressed={selectedController === 'ALL'}
                onPressedChange={() => setSelectedController('ALL')}
                className="gap-2 border h-8 px-3 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
              >
                <span>All</span>
              </Toggle>
              {availableControllers.map((controllerType) => {
                const config = CONTROLLER_CONFIG[controllerType];
                const IconComponent = config.icon;
                return (
                  <Toggle
                    key={controllerType}
                    pressed={selectedController === controllerType}
                    onPressedChange={() =>
                      setSelectedController(controllerType)
                    }
                    className="gap-2 border h-8 px-3 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
                  >
                    {IconComponent && <IconComponent className="w-3 h-3" />}
                    <span>{config.name}</span>
                  </Toggle>
                );
              })}
            </div>
          )}

          <DownloadConsentNotice
            id="pg-licence-accept"
            accepted={licenceAccepted}
            invalid={needsConsent}
            onAcceptedChange={(next) => {
              setLicenceAccepted(next);
              if (next) setNeedsConsent(false);
            }}
          >
            <a
              href="https://creativecommons.org/licenses/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-2 hover:text-primary"
              onClick={(e) => e.stopPropagation()}
            >
              Creative Commons license
            </a>{' '}
            <LiabilityWaiverDialog triggerText="terms" /> of each included
            project.
          </DownloadConsentNotice>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden sm:table-cell w-[50px]">
                    Type
                  </TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead className="w-[60px] text-center">Format</TableHead>
                  <TableHead className="w-[90px] text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => {
                    const rawAttachments = project.attachments || [];
                    // Found on the RAW list: the project zip backs "Download All"
                    // even though filterAttachments hides it from the file rows.
                    const projectZip = rawAttachments.find(
                      (a) => a.category === IAttachmentCategory.postZip
                    );

                    const listedAttachments = filterAttachments(rawAttachments);
                    const attachments =
                      selectedController === 'ALL'
                        ? listedAttachments
                        : filterAttachmentsByController(
                            listedAttachments,
                            selectedController
                          );

                    const gcodeFiles = attachments.filter(
                      (a) =>
                        a.resourceType === AttachmentType.model &&
                        a._id !== projectZip?._id
                    );

                    // Pictures: true raster images (excluding SVG, AI, etc.)
                    const pictureFiles = attachments.filter(
                      (a) =>
                        a.resourceType === AttachmentType.image &&
                        !isCadVectorFile(a.fileName) &&
                        a._id !== projectZip?._id
                    );

                    // CAD/Vectors: everything else (including SVG, AI, DXF, CRV, etc.)
                    const cadFiles = attachments.filter(
                      (a) =>
                        a.resourceType !== AttachmentType.model &&
                        (a.resourceType !== AttachmentType.image ||
                          isCadVectorFile(a.fileName)) &&
                        a._id !== projectZip?._id
                    );

                    return (
                      <div key={project._id} style={{ display: 'contents' }}>
                        {/* Project Header Row */}
                        <TableRow className="hover:bg-muted/50 bg-muted/30 border-b">
                          <TableCell
                            colSpan={2}
                            className="font-bold text-base py-3 px-4"
                          >
                            <div className="flex items-center gap-2">
                              <span className="truncate">{project.title}</span>
                              <span className="text-xs font-normal text-muted-foreground whitespace-nowrap">
                                ({attachments.length} files)
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs sm:text-sm text-muted-foreground">
                            {projectZip && getFileExtension(projectZip.url)}
                          </TableCell>
                          <TableCell>
                            {projectZip && (
                              <div className="bg-secondary rounded-full">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(projectZip, project.title);
                                  }}
                                  disabled={!!downloadingId}
                                  className={cn(
                                    'h-8 text-xs whitespace-nowrap',
                                    downloadsDisabled && 'opacity-60'
                                  )}
                                >
                                  {downloadingId === projectZip._id ? (
                                    <Loader2 className="mr-2 h-3 w-3 animate-spin text-white" />
                                  ) : (
                                    'Download All'
                                  )}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* GCode Files */}
                        <GcodeByControllerRows
                          files={gcodeFiles}
                          cellClassName="font-semibold text-sm py-2 px-6 text-muted-foreground"
                          renderRow={(attachment) => (
                            <AttachmentFileRow
                              key={attachment._id}
                              attachment={attachment}
                              projectTitle={project.title}
                              muted={downloadsDisabled}
                              downloadingId={downloadingId}
                              onDownload={handleDownload}
                            />
                          )}
                        />

                        {/* CAD / Vectors Files */}
                        {cadFiles.length > 0 && (
                          <>
                            <FileSectionToggleRow
                              label="CAD / Vectors"
                              count={cadFiles.length}
                              open={openCad}
                              onToggle={() => setOpenCad((v) => !v)}
                              cellClassName="font-semibold text-sm py-2 px-6 text-muted-foreground"
                            />
                            {(openCad ? cadFiles : []).map((attachment) => (
                              <AttachmentFileRow
                                key={attachment._id}
                                attachment={attachment}
                                projectTitle={project.title}
                                muted={downloadsDisabled}
                                downloadingId={downloadingId}
                                onDownload={handleDownload}
                              />
                            ))}
                          </>
                        )}

                        {/* Pictures */}
                        {pictureFiles.length > 0 && (
                          <>
                            <FileSectionToggleRow
                              label="Pictures"
                              count={pictureFiles.length}
                              open={openPics}
                              onToggle={() => setOpenPics((v) => !v)}
                              cellClassName="font-semibold text-sm py-2 px-6 text-muted-foreground"
                            />
                            {(openPics ? pictureFiles : []).map(
                              (attachment) => (
                                <AttachmentFileRow
                                  key={attachment._id}
                                  attachment={attachment}
                                  projectTitle={project.title}
                                  muted={downloadsDisabled}
                                  downloadingId={downloadingId}
                                  onDownload={handleDownload}
                                />
                              )
                            )}
                          </>
                        )}

                        {attachments.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="text-center text-muted-foreground py-4 text-sm italic"
                            >
                              No files available for this project
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Spacer Row */}
                        <TableRow className="h-4 border-none hover:bg-transparent">
                          <TableCell colSpan={4} className="p-0 border-none" />
                        </TableRow>
                      </div>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      No projects available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
